import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import _ from "lodash";
import Percent from "_shared/percentages/percent";
import { Masked, MaskedContracts, MaskedDiscount, MaskedFeeSchedule, MaskedTeam } from "work/masked-fields";
import "work/proposals/__extensions/users/user-extensions";
import { ProposalJSON, type ProposalNegotiationOperationJSON } from "work/proposals/_schemas/proposal-schema";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  ProposalFieldName,
  ProposalFieldNameValue,
} from "work/proposals/proposal-fields/proposal-field";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline, { RedlinedDates, RedlinedDocuments } from "work/proposals/redlining/proposal-redline";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";

export type LocalProposalJSON = ProposalJSON & {
  counterRevisionChangeOrigins?: ProposalCounterRevisionChangeOriginMap;
  counterRevisionChangeParties?: ProposalCounterRevisionChangePartyMap;
  existingRedlineJSON?: ProposalJSON["existingRedlineJSON"];
  hasLocallyMaterializedCounterRevision?: boolean;
  hasLocallyUnsavedRedlineChanges?: boolean;
};

export type ProposalCounterRevisionChangeOrigin = "browserLocal" | "persistedPartyRevision";
export type ProposalCounterRevisionChangeOriginMap = Partial<Record<string, ProposalCounterRevisionChangeOrigin>>;
export type ProposalCounterRevisionChangeParty = ProposalNegotiationOperationJSON["party"];
export type ProposalCounterRevisionChangePartyMap = Partial<Record<string, ProposalCounterRevisionChangeParty>>;

export type ProposalCounterRevisionFieldChange = {
  from: unknown;
  to: unknown;
};

export type ProposalCounterRevisionChangeSet = Partial<Record<string, ProposalCounterRevisionFieldChange>>;

export type ProposalCounterRevisionState = {
  baseFieldValues?: Partial<Record<string, unknown>>;
  baseProposalJSON?: ProposalJSON;
  baseRevisionId?: string;
  changeOrigins?: ProposalCounterRevisionChangeOriginMap;
  changeParties?: ProposalCounterRevisionChangePartyMap;
  changes?: ProposalCounterRevisionChangeSet;
  existingRedlineJSON?: ProposalJSON["existingRedlineJSON"];
  hasCurrentRevisionIntent?: boolean;
  hasLocallyUnsavedRedlineChanges?: boolean;
  proposalId?: string;
  unresolvedFields?: ProposalFieldNameValue[];
};

export type ProposalCounterRevisionReconciliation = {
  counterRevisionState?: ProposalCounterRevisionState;
  localProposalJSON?: LocalProposalJSON;
  shouldClearLocalProposalJSON: boolean;
};

type CounterRevisionCollectionKey = "categories" | "documents" | "members";

export default class ProposalRedlineFactory {
  public static reconcileCounterRevisionProposalJSON({
    existingCounterRevisionState,
    latestProposalJSON,
    localProposalJSON,
    partyRevisionSourceProposalJSON,
  }: {
    existingCounterRevisionState?: ProposalCounterRevisionState;
    latestProposalJSON?: ProposalJSON | null;
    localProposalJSON?: LocalProposalJSON;
    partyRevisionSourceProposalJSON?: ProposalJSON | readonly ProposalJSON[];
  }): ProposalCounterRevisionReconciliation {
    const latestProposalJSONValue = latestProposalJSON ?? undefined;
    const materializationBaseProposalJSON = latestProposalJSONValue ?? localProposalJSON;
    const shouldClearLocalProposalJSON = this.proposalJSONHasCounterRevisionState(localProposalJSON);
    const counterRevisionState = this.createCounterRevisionStateForReconciliation({
      existingCounterRevisionState,
      latestProposalJSON: latestProposalJSONValue,
      localProposalJSON,
      partyRevisionSourceProposalJSON,
    });
    const prunedCounterRevisionState = this.pruneCounterRevisionStateForLatestProposal({
      counterRevisionState,
      latestProposalJSON: materializationBaseProposalJSON,
    });
    const materializedCounterRevisionJSON = this.materializeCounterRevisionState({
      counterRevisionState: prunedCounterRevisionState,
      latestProposalJSON: materializationBaseProposalJSON,
    });

    return {
      counterRevisionState: prunedCounterRevisionState,
      localProposalJSON:
        materializedCounterRevisionJSON ?? (shouldClearLocalProposalJSON ? undefined : localProposalJSON),
      shouldClearLocalProposalJSON,
    };
  }

  public static fromProposal(proposal: Proposal): ProposalRedline | undefined {
    if (!proposal.id || !proposal.name || !proposal.description) return;

    const currentUserIsLatestSubmittingParty = proposal.currentUser.isLatestSubmittingParty(proposal);
    const currentUserHasApproved = proposal.currentUser.hasApproved(proposal);
    const partyRevision = this.partyRevisionForRedline(proposal);

    let originalVersion: Proposal = proposal;
    let revisedVersion: Proposal = proposal;
    let currentVersion: Proposal | undefined;

    if (proposal.supersedes && !proposal.isApprovedByBothParties) {
      originalVersion = new Proposal({
        currentUser: proposal.currentUser,
        spec: proposal.supersedes,
        options: {
          withRedline: false,
        },
      });
      revisedVersion = currentUserIsLatestSubmittingParty
        ? new Proposal({
            currentUser: proposal.currentUser,
            spec: {
              ...proposal.supersedes,
            },
            options: {
              withRedline: false,
            },
          })
        : proposal;
      currentVersion = currentUserIsLatestSubmittingParty ? proposal : undefined;
    }

    if (partyRevision?.lastUpdated?.isAfter(currentVersion?.lastUpdated) || (partyRevision && !currentVersion)) {
      currentVersion = new Proposal({
        currentUser: proposal.currentUser,
        spec: {
          ...partyRevision,
        },
        options: {
          withRedline: false,
        },
      });
    }

    if (currentUserHasApproved && proposal.isApprovedByBothParties) {
      originalVersion = proposal;
      revisedVersion = proposal;
      currentVersion ??= proposal;
    }

    const nameRedline = new FieldRedline<ProjectName>({
      field: ProposalField.Name,
      originalEntry: originalVersion.name ?? null,
      revisedEntry: revisedVersion.name ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Name) ? undefined : currentVersion?.name,
      diffWords: true,
    });

    const descriptionRedline = new FieldRedline<ProjectDescription>({
      field: ProposalField.Description,
      originalEntry: originalVersion.description ?? null,
      revisedEntry: revisedVersion.description ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Description)
        ? undefined
        : currentVersion?.description,
      diffWords: true,
    });

    const feeScheduleUnresolved = currentVersion?.unresolvedFields.has(ProposalFieldName.FeeSchedule);
    const feeScheduleRedline =
      Masked.isMasked(originalVersion?.feeSchedule) ||
      Masked.isMasked(revisedVersion.feeSchedule) ||
      Masked.isMasked(currentVersion?.feeSchedule)
        ? new MaskedFeeSchedule()
        : new FeeScheduleRedline(
            originalVersion.feeSchedule,
            revisedVersion.feeSchedule,
            feeScheduleUnresolved ? undefined : currentVersion?.feeSchedule,
          );

    const teamRestrictionUnresolved = currentVersion?.unresolvedFields.has(ProposalFieldName.TeamRestriction);
    const teamRestrictedRedline =
      Masked.isMasked(originalVersion.teamRestricted) ||
      Masked.isMasked(revisedVersion.teamRestricted) ||
      Masked.isMasked(currentVersion?.teamRestricted)
        ? new MaskedTeam()
        : new FieldRedline<AHBoolean>({
            field: ProposalField.TeamRestriction,
            originalEntry: originalVersion.teamRestricted ?? null,
            revisedEntry: revisedVersion.teamRestricted ?? null,
            currentEntry: teamRestrictionUnresolved ? undefined : currentVersion?.teamRestricted,
            diffWords: false,
          });

    const conflictsCheckWaivedUnresolved = currentVersion?.unresolvedFields.has(ProposalFieldName.WaiveConflictsCheck);
    const conflictsCheckWaivedRedline =
      Masked.isMasked(originalVersion.conflictsCheckWaived) ||
      Masked.isMasked(revisedVersion.conflictsCheckWaived) ||
      Masked.isMasked(currentVersion?.conflictsCheckWaived)
        ? new MaskedContracts()
        : new FieldRedline<AHBoolean>({
            field: ProposalField.WaiveConflictsCheck,
            originalEntry: originalVersion.conflictsCheckWaived,
            revisedEntry: revisedVersion.conflictsCheckWaived,
            currentEntry: conflictsCheckWaivedUnresolved ? undefined : currentVersion?.conflictsCheckWaived,
            diffWords: false,
          });

    const waivingConflictsChecks = Boolean(
      Masked.isNotMasked(conflictsCheckWaivedRedline) &&
      conflictsCheckWaivedRedline.isAccepted &&
      conflictsCheckWaivedRedline.currentEntry?.isEqualTo(new AHBoolean(true)),
    );

    const discountRedline =
      Masked.isMasked(originalVersion.discount) ||
      Masked.isMasked(revisedVersion.discount) ||
      Masked.isMasked(currentVersion?.discount)
        ? (originalVersion.discount as MaskedDiscount)
        : new FieldRedline<Percent>({
            field: ProposalField.Discount,
            originalEntry: originalVersion.discount ?? null,
            revisedEntry: revisedVersion.discount ?? null,
            currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Discount)
              ? undefined
              : currentVersion?.discount,
            diffWords: false,
          });

    return new ProposalRedline({
      name: nameRedline,
      description: descriptionRedline,
      ...this.createDatesRedline({
        originalVersion,
        revisedVersion,
        currentVersion,
      }),
      vendorTeam: this.createTeamRedline({
        originalVersion,
        revisedVersion,
        currentVersion,
        missingRevisedEntriesAreRejected: currentVersion?.lastUpdated?.isAfter(revisedVersion.lastUpdated) ?? false,
      }),
      feeSchedule: feeScheduleRedline,
      conflictsCheckWaived: conflictsCheckWaivedRedline,
      teamRestricted: teamRestrictedRedline,
      ...this.createDocumentsRedline({
        originalVersion,
        revisedVersion,
        currentVersion,
        waivingConflictsChecks,
        missingRevisedEntriesAreRejected: currentVersion?.lastUpdated?.isAfter(revisedVersion.lastUpdated) ?? false,
      }),
      discount: discountRedline,
      lastUpdated: new AHDate(),
    });
  }

  private static createCounterRevisionStateForReconciliation({
    existingCounterRevisionState,
    latestProposalJSON,
    localProposalJSON,
    partyRevisionSourceProposalJSON,
  }: {
    existingCounterRevisionState?: ProposalCounterRevisionState;
    latestProposalJSON?: ProposalJSON;
    localProposalJSON?: LocalProposalJSON;
    partyRevisionSourceProposalJSON?: ProposalJSON | readonly ProposalJSON[];
  }): ProposalCounterRevisionState | undefined {
    const baseProposalJSON = latestProposalJSON ?? localProposalJSON;
    const states: ProposalCounterRevisionState[] = [];
    const browserLocalStates: ProposalCounterRevisionState[] = [];

    if (existingCounterRevisionState) {
      const existingState = this.cloneJSONValue(existingCounterRevisionState);
      (this.counterRevisionStateHasLocalMarkers(existingState) ? browserLocalStates : states).push(existingState);
    }

    if (localProposalJSON && this.proposalJSONHasCounterRevisionState(localProposalJSON)) {
      const localCounterRevisionState = this.createCounterRevisionState({
        baseProposalJSON: localProposalJSON,
        localProposalJSON,
        partyRevisionJSON: localProposalJSON.partyRevision,
      });
      if (localCounterRevisionState) {
        (this.counterRevisionStateHasLocalMarkers(localCounterRevisionState) ? browserLocalStates : states).push(
          localCounterRevisionState,
        );
      }
    }

    if (!baseProposalJSON) {
      return this.mergeCounterRevisionStates([...states, ...browserLocalStates]);
    }

    const shouldUsePartyRevisionSources =
      !localProposalJSON ||
      Boolean(existingCounterRevisionState) ||
      this.proposalJSONHasCounterRevisionState(localProposalJSON);

    if (shouldUsePartyRevisionSources) {
      for (const sourceProposalJSON of this.normalizePartyRevisionSourceProposalJSONs(
        partyRevisionSourceProposalJSON,
      )) {
        if (!sourceProposalJSON.partyRevision) continue;

        const sourceCounterRevisionState = this.createCounterRevisionState({
          baseProposalJSON: sourceProposalJSON,
          partyRevisionJSON: sourceProposalJSON.partyRevision,
        });
        if (sourceCounterRevisionState) {
          states.push(sourceCounterRevisionState);
        }
      }
    }

    // A same-revision refresh may contain an older saved party revision. It is
    // authoritative over unmarked cached server-derived state, but it must not
    // overwrite counter-revision work explicitly marked as browser-local.
    return this.mergeCounterRevisionStates([...states, ...browserLocalStates]);
  }

  private static createCounterRevisionState({
    baseProposalJSON,
    localProposalJSON,
    partyRevisionJSON,
  }: {
    baseProposalJSON: ProposalJSON;
    localProposalJSON?: LocalProposalJSON;
    partyRevisionJSON?: ProposalJSON | null;
  }): ProposalCounterRevisionState | undefined {
    const state: ProposalCounterRevisionState = {
      baseFieldValues: this.extractCounterRevisionBaseValues(baseProposalJSON),
      baseProposalJSON: this.stripRevisionPointers(baseProposalJSON),
      baseRevisionId: this.proposalRevisionId(baseProposalJSON),
      hasCurrentRevisionIntent:
        Boolean(localProposalJSON) ||
        Boolean((baseProposalJSON as LocalProposalJSON).hasLocallyMaterializedCounterRevision) ||
        Boolean((baseProposalJSON as LocalProposalJSON).hasLocallyUnsavedRedlineChanges) ||
        this.proposalJSONHasCurrentPartyRevisionIntent(baseProposalJSON),
      proposalId: baseProposalJSON.latestRevisionId ?? baseProposalJSON.id ?? undefined,
    };

    this.copyCounterRevisionStateLocalMarkers(state, localProposalJSON);
    if (partyRevisionJSON) {
      state.changes = this.extractCounterRevisionChangeSet(baseProposalJSON, partyRevisionJSON);
      if (state.changes) {
        const localStateJSON = (localProposalJSON ?? baseProposalJSON) as LocalProposalJSON;
        state.changeOrigins = this.createCounterRevisionChangeMetadataMap({
          changes: state.changes,
          fallbackValue: localProposalJSON?.hasLocallyUnsavedRedlineChanges ? "browserLocal" : "persistedPartyRevision",
          inheritedValues: localStateJSON.counterRevisionChangeOrigins,
        });
        state.changeParties = this.createCounterRevisionChangeMetadataMap({
          changes: state.changes,
          fallbackValue: this.proposalJSONCurrentPartyRevisionParty(baseProposalJSON),
          inheritedValues: localStateJSON.counterRevisionChangeParties,
        });
      }
      state.unresolvedFields = state.changes
        ? this.counterRevisionUnresolvedFieldsFromProposalJSON(partyRevisionJSON)
        : undefined;
    }

    return this.counterRevisionStateHasContent(state) ? state : undefined;
  }

  private static createCounterRevisionChangeMetadataMap<T extends string>({
    changes,
    fallbackValue,
    inheritedValues,
  }: {
    changes: ProposalCounterRevisionChangeSet;
    fallbackValue?: T;
    inheritedValues?: Partial<Record<string, T>>;
  }): Partial<Record<string, T>> | undefined {
    const values: Partial<Record<string, T>> = {};
    for (const key of Object.keys(changes)) {
      const value = inheritedValues?.[key] ?? fallbackValue;
      if (value) values[key] = value;
    }

    return Object.keys(values).length > 0 ? values : undefined;
  }

  private static normalizePartyRevisionSourceProposalJSONs(
    partyRevisionSourceProposalJSON: ProposalJSON | readonly ProposalJSON[] | undefined,
  ): readonly ProposalJSON[] {
    if (!partyRevisionSourceProposalJSON) return [];
    return this.isProposalJSONArray(partyRevisionSourceProposalJSON)
      ? partyRevisionSourceProposalJSON
      : [partyRevisionSourceProposalJSON];
  }

  private static isProposalJSONArray(value: ProposalJSON | readonly ProposalJSON[]): value is readonly ProposalJSON[] {
    return Array.isArray(value);
  }

  private static mergeCounterRevisionStates(
    states: readonly ProposalCounterRevisionState[],
  ): ProposalCounterRevisionState | undefined {
    const [firstState, ...restStates] = states;
    if (!firstState) return undefined;

    const mergedState = this.cloneJSONValue(firstState);
    for (const state of restStates) {
      this.mergeCounterRevisionStateInto(mergedState, state);
    }

    return this.counterRevisionStateHasContent(mergedState) ? mergedState : undefined;
  }

  private static mergeCounterRevisionStateInto(
    target: ProposalCounterRevisionState,
    source: ProposalCounterRevisionState,
  ): void {
    const sourceHasLocalMarkers = this.counterRevisionStateHasLocalMarkers(source);
    if ((!this.counterRevisionStateHasLocalMarkers(target) || sourceHasLocalMarkers) && source.baseRevisionId) {
      target.baseRevisionId = source.baseRevisionId;
    }
    if (source.baseProposalJSON) {
      target.baseProposalJSON = this.cloneProposalJSON(source.baseProposalJSON);
    }
    if (source.baseFieldValues) {
      target.baseFieldValues = {
        ...(target.baseFieldValues ?? {}),
        ...this.cloneJSONValue(source.baseFieldValues),
      };
    }
    if (source.proposalId) {
      target.proposalId = source.proposalId;
    }
    if (source.existingRedlineJSON) {
      target.existingRedlineJSON = this.cloneJSONValue(source.existingRedlineJSON);
    }
    if (source.hasCurrentRevisionIntent) {
      target.hasCurrentRevisionIntent = true;
    }
    if (source.hasLocallyUnsavedRedlineChanges) {
      target.hasLocallyUnsavedRedlineChanges = true;
    }

    this.clearChangesRevertedByBrowserSnapshot(target, source);
    if (source.changes) {
      const sourceChanges = this.normalizeBrowserLocalChangesForMerge(target, source);
      const overwrittenUnresolvedFields = this.unresolvedFieldsForCounterRevisionChangeKeys(Object.keys(sourceChanges));
      target.unresolvedFields = this.pruneCounterRevisionUnresolvedFields(
        target.unresolvedFields,
        overwrittenUnresolvedFields,
      );
      this.mergeCounterRevisionChangeMetadata(target, {
        ...source,
        changes: sourceChanges,
      });
      target.changes = {
        ...(target.changes ?? {}),
        ...sourceChanges,
      };
    }

    target.unresolvedFields = this.mergeCounterRevisionUnresolvedFields(
      target.unresolvedFields,
      source.unresolvedFields,
    );
  }

  private static clearChangesRevertedByBrowserSnapshot(
    target: ProposalCounterRevisionState,
    source: ProposalCounterRevisionState,
  ): void {
    if (
      !source.hasLocallyUnsavedRedlineChanges ||
      !target.changes ||
      !source.baseFieldValues ||
      !source.baseProposalJSON
    ) {
      return;
    }

    const sourceParty = this.proposalJSONCurrentPartyRevisionParty(source.baseProposalJSON);
    if (!sourceParty) return;

    const removedUnresolvedFields = new Set<ProposalFieldNameValue>();
    for (const proposalField of this.counterRevisionFieldsWithChanges(target.changes)) {
      const snapshotRevertedField = proposalField.proposalJSONKeys.some(
        (key) =>
          this.getCounterRevisionChange(target.changes, key) &&
          !this.getCounterRevisionChange(source.changes, key) &&
          this.counterRevisionStateHasBaseFieldValue(source.baseFieldValues!, key) &&
          target.changeParties?.[key] === sourceParty &&
          !this.counterRevisionValueIsMasked(source.baseFieldValues?.[key]),
      );
      if (!snapshotRevertedField) continue;

      this.removeCounterRevisionFieldChanges(target.changes, proposalField, target);
      this.addUnresolvedFieldNames(removedUnresolvedFields, proposalField.unresolvedFieldNames);
    }

    target.unresolvedFields = this.pruneCounterRevisionUnresolvedFields(
      target.unresolvedFields,
      removedUnresolvedFields,
    );
    if (!this.counterRevisionChangeSetHasChanges(target.changes)) {
      target.changes = undefined;
    }
  }

  private static counterRevisionValueIsMasked(value: unknown): boolean {
    return Boolean(value && typeof value === "object" && (value as { isMasked?: unknown }).isMasked === true);
  }

  private static normalizeBrowserLocalChangesForMerge(
    target: ProposalCounterRevisionState,
    source: ProposalCounterRevisionState,
  ): ProposalCounterRevisionChangeSet {
    const normalizedChanges = this.cloneJSONValue(source.changes ?? {});
    for (const [key, sourceChange] of Object.entries(normalizedChanges)) {
      if (!sourceChange) continue;
      if (source.changeOrigins?.[key] !== "browserLocal") continue;

      const targetChange = target.changes?.[key];
      if (!targetChange) continue;

      const sourceParty = source.changeParties?.[key];
      const targetParty = target.changeParties?.[key];
      if (sourceParty && targetParty && sourceParty !== targetParty) continue;

      if (target.changeOrigins?.[key] === "persistedPartyRevision") {
        if (
          !this.counterRevisionChangeKeyIsUnresolved(target, key) &&
          !_.isEqual(targetChange, sourceChange)
        ) {
          // The local snapshot contains both the saved party draft and edits
          // made after that save. Retain only the post-save delta so a later
          // submission can consume the saved layer without losing browser work.
          sourceChange.from = this.cloneJSONValue(targetChange.to);
        }
        continue;
      }

      if (target.changeOrigins?.[key] === "browserLocal") {
        // Proposal.toJSON() recreates each browser snapshot against the server
        // base. Keep the already-normalized post-save baseline through later
        // local edits instead of widening the change back to that server base.
        sourceChange.from = this.cloneJSONValue(targetChange.from);
      }
    }

    return normalizedChanges;
  }

  private static counterRevisionChangeKeyIsUnresolved(
    counterRevisionState: ProposalCounterRevisionState,
    key: string,
  ): boolean {
    if (!counterRevisionState.unresolvedFields?.length) return false;

    const unresolvedFields = new Set(counterRevisionState.unresolvedFields);
    return ProposalField.CounterRevisionFields.some(
      (proposalField) =>
        proposalField.proposalJSONKeys.includes(key) &&
        proposalField.unresolvedFieldNames.some((fieldName) => unresolvedFields.has(fieldName)),
    );
  }

  private static mergeCounterRevisionChangeMetadata(
    target: ProposalCounterRevisionState,
    source: ProposalCounterRevisionState,
  ): void {
    if (!source.changes) return;

    const mergedOrigins = { ...(target.changeOrigins ?? {}) };
    const mergedParties = { ...(target.changeParties ?? {}) };
    for (const [key, sourceChange] of Object.entries(source.changes)) {
      const targetChange = target.changes?.[key];
      const changesAreEqual = _.isEqual(targetChange, sourceChange);
      const sourceOrigin = source.changeOrigins?.[key];
      const targetOrigin = target.changeOrigins?.[key];
      if (sourceOrigin && !(sourceOrigin === "browserLocal" && targetOrigin && changesAreEqual)) {
        mergedOrigins[key] = sourceOrigin;
      } else if (!sourceOrigin && !changesAreEqual) {
        delete mergedOrigins[key];
      }

      const sourceParty = source.changeParties?.[key];
      if (sourceParty) {
        mergedParties[key] = sourceParty;
      } else if (!changesAreEqual) {
        delete mergedParties[key];
      }
    }

    target.changeOrigins = Object.keys(mergedOrigins).length > 0 ? mergedOrigins : undefined;
    target.changeParties = Object.keys(mergedParties).length > 0 ? mergedParties : undefined;
  }

  private static unresolvedFieldsForCounterRevisionChangeKeys(
    changeKeys: readonly string[],
  ): Set<ProposalFieldNameValue> {
    const unresolvedFields = new Set<ProposalFieldNameValue>();
    const changeKeySet = new Set(changeKeys);
    for (const proposalField of ProposalField.CounterRevisionFields) {
      if (proposalField.proposalJSONKeys.some((key) => changeKeySet.has(key))) {
        this.addUnresolvedFieldNames(unresolvedFields, proposalField.unresolvedFieldNames);
      }
    }
    return unresolvedFields;
  }

  private static mergeCounterRevisionUnresolvedFields(
    targetUnresolvedFields: ProposalFieldNameValue[] | undefined,
    sourceUnresolvedFields: ProposalFieldNameValue[] | undefined,
  ): ProposalFieldNameValue[] | undefined {
    if (!targetUnresolvedFields?.length) {
      return sourceUnresolvedFields?.length ? [...sourceUnresolvedFields] : undefined;
    }
    if (!sourceUnresolvedFields?.length) return targetUnresolvedFields;

    return Array.from(new Set([...targetUnresolvedFields, ...sourceUnresolvedFields]));
  }

  private static pruneCounterRevisionStateForLatestProposal({
    counterRevisionState,
    latestProposalJSON,
  }: {
    counterRevisionState: ProposalCounterRevisionState | undefined;
    latestProposalJSON: ProposalJSON | undefined;
  }): ProposalCounterRevisionState | undefined {
    if (!counterRevisionState || !latestProposalJSON) {
      return counterRevisionState;
    }

    const prunedState = this.cloneJSONValue(counterRevisionState);
    if (!prunedState.hasCurrentRevisionIntent && this.counterRevisionStateHasLocalMarkers(prunedState)) {
      // Counter-revision snapshots persisted before intent provenance was
      // introduced still identify genuine browser-local work through these
      // markers. Preserve that provenance before revision-scoped marker data
      // is cleared while rebasing onto a confirmed successor.
      prunedState.hasCurrentRevisionIntent = true;
    }
    this.clearStaleCounterRevisionLocalMarkers(prunedState, latestProposalJSON);

    const changes = prunedState.changes;
    if (!changes) return this.counterRevisionStateHasContent(prunedState) ? prunedState : undefined;

    const removedUnresolvedFields = new Set<ProposalFieldNameValue>();
    const conflictingUnresolvedFields = new Set<ProposalFieldNameValue>();
    for (const proposalField of this.counterRevisionFieldsWithChanges(changes)) {
      this.rebaseBrowserLocalFieldAfterSamePartySubmission({
        counterRevisionState: prunedState,
        latestProposalJSON,
        proposalField,
      });
      if (
        this.latestProposalConsumesPersistedCounterRevisionField({
          counterRevisionState: prunedState,
          latestProposalJSON,
          proposalField,
        })
      ) {
        this.removeCounterRevisionFieldChanges(changes, proposalField, prunedState);
        this.addUnresolvedFieldNames(removedUnresolvedFields, proposalField.unresolvedFieldNames);
        continue;
      }

      if (this.counterRevisionFieldStillApplies(proposalField, changes, latestProposalJSON, prunedState)) {
        if (this.counterRevisionFieldHasIncomingConflict(proposalField, changes, latestProposalJSON, prunedState)) {
          this.addUnresolvedFieldNames(conflictingUnresolvedFields, proposalField.unresolvedFieldNames);
        }
        continue;
      }

      this.removeCounterRevisionFieldChanges(changes, proposalField, prunedState);
      this.addUnresolvedFieldNames(removedUnresolvedFields, proposalField.unresolvedFieldNames);
    }

    if (this.counterRevisionChangeSetHasChanges(changes)) {
      prunedState.unresolvedFields = this.pruneCounterRevisionUnresolvedFields(
        prunedState.unresolvedFields,
        removedUnresolvedFields,
      );
      prunedState.unresolvedFields = this.mergeCounterRevisionUnresolvedFields(
        prunedState.unresolvedFields,
        Array.from(conflictingUnresolvedFields),
      );
    } else {
      prunedState.changes = undefined;
      prunedState.changeOrigins = undefined;
      prunedState.changeParties = undefined;
      prunedState.unresolvedFields = undefined;
    }

    return this.counterRevisionStateHasContent(prunedState) ? prunedState : undefined;
  }

  private static materializeCounterRevisionState({
    counterRevisionState,
    latestProposalJSON,
  }: {
    counterRevisionState: ProposalCounterRevisionState | undefined;
    latestProposalJSON: ProposalJSON | undefined;
  }): LocalProposalJSON | undefined {
    const prunedState = this.pruneCounterRevisionStateForLatestProposal({
      counterRevisionState,
      latestProposalJSON,
    });
    if (!prunedState || !latestProposalJSON) return undefined;

    const proposalJSON: LocalProposalJSON = this.cloneProposalJSON(latestProposalJSON);
    proposalJSON.existingRedlineJSON = undefined;
    if (!proposalJSON.supersedes && prunedState.baseProposalJSON) {
      proposalJSON.supersedes = this.cloneProposalJSON(prunedState.baseProposalJSON);
    }
    this.copyCounterRevisionLocalMarkers(proposalJSON, prunedState);
    proposalJSON.counterRevisionChangeOrigins = prunedState.changeOrigins
      ? this.cloneJSONValue(prunedState.changeOrigins)
      : undefined;
    proposalJSON.counterRevisionChangeParties = prunedState.changeParties
      ? this.cloneJSONValue(prunedState.changeParties)
      : undefined;
    if (!prunedState.changes) {
      return this.counterRevisionStateHasLocalMarkers(prunedState) ? proposalJSON : undefined;
    }

    const partyRevisionJSON = this.stripRevisionPointers(latestProposalJSON);
    const unresolvedFields = new Set<ProposalFieldNameValue>();
    const changes = prunedState.changes;
    const materializedFields = this.counterRevisionFieldsWithChanges(changes).filter((proposalField) =>
      this.counterRevisionFieldStillApplies(proposalField, changes, latestProposalJSON, prunedState),
    );
    const materializedFieldNames = new Set(
      materializedFields.flatMap((proposalField) => proposalField.proposalJSONKeys),
    );

    for (const proposalField of ProposalField.CounterRevisionFields) {
      if (proposalField.proposalJSONKeys.some((key) => materializedFieldNames.has(key))) continue;

      if (
        this.writeBaseFieldValuesForIncomingChanges({
          baseFieldValues: prunedState.baseFieldValues,
          latestProposalJSON,
          partyRevisionJSON,
          proposalField,
        })
      ) {
        this.addUnresolvedFieldNames(unresolvedFields, proposalField.unresolvedFieldNames);
      }
    }

    for (const proposalField of materializedFields) {
      this.writeCounterRevisionFieldValues(partyRevisionJSON, proposalField, changes, latestProposalJSON);
      this.addMaterializedUnresolvedFields(unresolvedFields, proposalField, prunedState.unresolvedFields);
      this.writeIncomingConflictBaseline({
        changes,
        counterRevisionState: prunedState,
        latestProposalJSON,
        proposalField,
        proposalJSON,
      });
    }

    const hasPartyRevisionValues = materializedFields.some((proposalField) =>
      this.counterRevisionFieldDiffersFromLatest(proposalField, latestProposalJSON, partyRevisionJSON),
    );
    if (hasPartyRevisionValues) {
      proposalJSON.hasLocallyMaterializedCounterRevision = true;
      partyRevisionJSON.unresolvedFields = unresolvedFields.size > 0 ? Array.from(unresolvedFields) : undefined;
      proposalJSON.partyRevision = partyRevisionJSON;
    }

    return hasPartyRevisionValues || this.counterRevisionStateHasLocalMarkers(prunedState) ? proposalJSON : undefined;
  }

  private static createDatesRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
  }): RedlinedDates {
    const { originalVersion, revisedVersion, currentVersion } = params;
    let currentResponseDueBy: AHDate | null | undefined = currentVersion?.unresolvedFields.has(
      ProposalFieldName.ResponseDueBy,
    )
      ? undefined
      : currentVersion?.responseDueBy;
    if (currentVersion === undefined && !originalVersion.responseDueBy && !revisedVersion.responseDueBy) {
      currentResponseDueBy = null;
    }

    let currentStartDate: AHDate | null | undefined = currentVersion?.unresolvedFields.has(ProposalFieldName.StartDate)
      ? undefined
      : currentVersion?.startDate;
    if (currentVersion === undefined && !originalVersion.startDate && !revisedVersion.startDate) {
      currentStartDate = null;
    }

    let currentEndDate: AHDate | null | undefined = currentVersion?.unresolvedFields.has(ProposalFieldName.EndDate)
      ? undefined
      : currentVersion?.endDate;
    if (currentVersion === undefined && !originalVersion.endDate && !revisedVersion.endDate) {
      currentEndDate = null;
    }

    return {
      responseDueBy: new FieldRedline({
        field: ProposalField.ResponseDueBy,
        originalEntry: originalVersion.responseDueBy ?? null,
        revisedEntry: revisedVersion.responseDueBy ?? null,
        currentEntry: currentResponseDueBy,
        diffWords: false,
      }),
      startDate: new FieldRedline({
        field: ProposalField.StartDate,
        originalEntry: originalVersion.startDate ?? null,
        revisedEntry: revisedVersion.startDate ?? null,
        currentEntry: currentStartDate,
        diffWords: false,
      }),
      endDate: new FieldRedline({
        field: ProposalField.EndDate,
        originalEntry: originalVersion.endDate ?? null,
        revisedEntry: revisedVersion.endDate ?? null,
        currentEntry: currentEndDate,
        diffWords: false,
      }),
    };
  }

  private static cloneJSONValue<T>(value: T): T {
    return _.cloneDeep(value);
  }

  private static cloneProposalJSON(proposalJSON: ProposalJSON): ProposalJSON {
    return this.cloneJSONValue(proposalJSON);
  }

  private static proposalRevisionId(proposalJSON: ProposalJSON): string | undefined {
    return proposalJSON.id ?? proposalJSON.latestRevisionId ?? undefined;
  }

  private static proposalJSONHasCurrentPartyRevisionIntent(proposalJSON: ProposalJSON): boolean {
    if (!proposalJSON.partyRevision) return false;
    if (!proposalJSON.supersedes) return true;

    return Boolean(this.proposalJSONCurrentPartyRevisionOperation(proposalJSON));
  }

  private static proposalJSONCurrentPartyRevisionParty(
    proposalJSON: ProposalJSON,
  ): ProposalCounterRevisionChangeParty | undefined {
    return this.proposalJSONCurrentPartyRevisionOperation(proposalJSON)?.party;
  }

  private static proposalJSONCurrentPartyRevisionOperation(
    proposalJSON: ProposalJSON,
  ): ProposalNegotiationOperationJSON | undefined {
    const revisionId = this.proposalRevisionId(proposalJSON);
    if (!revisionId) return undefined;

    const partyRevisionId = proposalJSON.partyRevision?.id;
    const operations = proposalJSON.negotiationOperations ?? [];
    for (let index = operations.length - 1; index >= 0; index -= 1) {
      const operation = operations[index];
      if (
        operation.baseRevisionId !== revisionId ||
        (operation.kind !== "PartyRevisionPatched" && operation.kind !== "PartyRevisionReplaced") ||
        (partyRevisionId && operation.partyRevisionId && operation.partyRevisionId !== partyRevisionId)
      ) {
        continue;
      }

      return operation;
    }

    return undefined;
  }

  private static partyRevisionForRedline(proposal: Proposal): Proposal["partyRevision"] {
    if (!proposal.partyRevision) return undefined;
    if (!proposal.supersedes) return proposal.partyRevision;
    if (this.proposalHasLocalCounterRevisionMarker(proposal)) return proposal.partyRevision;
    if (this.proposalHasCurrentRevisionNegotiationOperations(proposal)) return proposal.partyRevision;
    if (!proposal.currentUser.isLatestSubmittingParty(proposal)) {
      return this.rebasedPartyRevisionForSupersedingProposal(proposal);
    }

    return undefined;
  }

  private static rebasedPartyRevisionForSupersedingProposal(proposal: Proposal): Proposal["partyRevision"] | undefined {
    if (!proposal.partyRevision || !proposal.supersedes) return undefined;

    try {
      const latestProposalJSON = proposal.toJSON();
      latestProposalJSON.existingRedlineJSON = undefined;
      latestProposalJSON.partyRevision = undefined;

      const partyRevisionSourceProposalJSON = new Proposal({
        currentUser: proposal.currentUser,
        spec: {
          ...proposal.supersedes,
          partyRevision: proposal.partyRevision,
        },
        options: {
          withRedline: false,
        },
      }).toJSON();
      partyRevisionSourceProposalJSON.existingRedlineJSON = undefined;

      const { localProposalJSON } = this.reconcileCounterRevisionProposalJSON({
        latestProposalJSON,
        partyRevisionSourceProposalJSON,
      });
      const rebasedPartyRevisionJSON = localProposalJSON?.partyRevision;
      if (!rebasedPartyRevisionJSON) return undefined;

      return Proposal.fromJSON({
        json: rebasedPartyRevisionJSON,
        currentUser: proposal.currentUser,
        options: {
          withRedline: false,
        },
      })?.spec;
    } catch {
      return undefined;
    }
  }

  private static proposalHasLocalCounterRevisionMarker(proposal: Proposal): boolean {
    return Boolean(
      (
        proposal as Proposal & {
          hasLocallyMaterializedCounterRevision?: boolean;
          hasLocallyUnsavedRedlineChanges?: boolean;
        }
      ).hasLocallyMaterializedCounterRevision ||
      (proposal as Proposal & { hasLocallyUnsavedRedlineChanges?: boolean }).hasLocallyUnsavedRedlineChanges,
    );
  }

  private static proposalHasCurrentRevisionNegotiationOperations(proposal: Proposal): boolean {
    const revisionId = proposal.id?.value ?? proposal.latestRevisionId?.value;
    return Boolean(
      revisionId &&
      proposal.negotiationOperations.some(
        (operation) =>
          operation.baseRevisionId === revisionId &&
          (operation.kind === "PartyRevisionPatched" || operation.kind === "PartyRevisionReplaced"),
      ),
    );
  }

  private static proposalJSONValue(proposalJSON: ProposalJSON | undefined, key: string): unknown {
    return (proposalJSON as Record<string, unknown> | undefined)?.[key];
  }

  private static writeProposalJSONValue(proposalJSON: ProposalJSON, key: string, value: unknown): void {
    (proposalJSON as Record<string, unknown>)[key] = this.cloneJSONValue(value);
  }

  private static copyCounterRevisionStateLocalMarkers(
    counterRevisionState: ProposalCounterRevisionState,
    localProposalJSON: LocalProposalJSON | undefined,
  ): void {
    if (localProposalJSON?.existingRedlineJSON) {
      counterRevisionState.existingRedlineJSON = this.cloneJSONValue(localProposalJSON.existingRedlineJSON);
    }
    if (localProposalJSON?.hasLocallyUnsavedRedlineChanges) {
      counterRevisionState.hasLocallyUnsavedRedlineChanges = true;
    }
  }

  private static clearStaleCounterRevisionLocalMarkers(
    counterRevisionState: ProposalCounterRevisionState,
    latestProposalJSON: ProposalJSON,
  ): void {
    const stateBaseRevisionId = counterRevisionState.baseRevisionId;
    const latestRevisionId = this.proposalRevisionId(latestProposalJSON);
    if (!stateBaseRevisionId || !latestRevisionId || stateBaseRevisionId === latestRevisionId) return;

    counterRevisionState.existingRedlineJSON = undefined;
    counterRevisionState.hasLocallyUnsavedRedlineChanges = undefined;
  }

  private static counterRevisionChangeSetHasChanges(changes: ProposalCounterRevisionChangeSet | undefined): boolean {
    return Boolean(changes && Object.keys(changes).length > 0);
  }

  private static getCounterRevisionChange(
    changes: ProposalCounterRevisionChangeSet | undefined,
    key: string,
  ): ProposalCounterRevisionFieldChange | undefined {
    if (!changes) return undefined;
    return Object.prototype.hasOwnProperty.call(changes, key) ? changes[key] : undefined;
  }

  private static counterRevisionFieldsWithChanges(changes: ProposalCounterRevisionChangeSet): ProposalField[] {
    return ProposalField.CounterRevisionFields.filter((proposalField) =>
      this.proposalFieldHasCounterRevisionChange(proposalField, changes),
    );
  }

  private static stripRevisionPointers(proposalJSON: ProposalJSON): ProposalJSON {
    const strippedProposalJSON = this.cloneProposalJSON(proposalJSON) as LocalProposalJSON;
    strippedProposalJSON.counterRevisionChangeOrigins = undefined;
    strippedProposalJSON.counterRevisionChangeParties = undefined;
    strippedProposalJSON.existingRedlineJSON = undefined;
    strippedProposalJSON.partyRevision = undefined;
    strippedProposalJSON.supersedes = undefined;
    return strippedProposalJSON;
  }

  private static counterRevisionUnresolvedFieldsFromProposalJSON(
    proposalJSON: ProposalJSON | undefined,
  ): ProposalFieldNameValue[] {
    const validCounterRevisionUnresolvedFields = new Set(
      ProposalField.CounterRevisionFields.flatMap((proposalField) => proposalField.unresolvedFieldNames),
    );

    return (proposalJSON?.unresolvedFields ?? [])
      .map((fieldNameValue) => fieldNameValue as ProposalFieldNameValue)
      .filter((fieldNameValue): fieldNameValue is ProposalFieldNameValue =>
        validCounterRevisionUnresolvedFields.has(fieldNameValue),
      );
  }

  private static copyCounterRevisionLocalMarkers(
    proposalJSON: LocalProposalJSON,
    counterRevisionState: ProposalCounterRevisionState,
  ): LocalProposalJSON {
    if (counterRevisionState.existingRedlineJSON) {
      proposalJSON.existingRedlineJSON = this.cloneJSONValue(counterRevisionState.existingRedlineJSON);
    }
    if (counterRevisionState.hasLocallyUnsavedRedlineChanges) {
      proposalJSON.hasLocallyUnsavedRedlineChanges = true;
    }
    return proposalJSON;
  }

  private static addUnresolvedFieldNames(
    unresolvedFields: Set<ProposalFieldNameValue>,
    fieldNames: readonly ProposalFieldNameValue[],
  ): void {
    for (const fieldName of fieldNames) {
      unresolvedFields.add(fieldName);
    }
  }

  private static proposalFieldHasCounterRevisionChange(
    proposalField: ProposalField,
    changes: ProposalCounterRevisionChangeSet | undefined,
  ): boolean {
    return proposalField.proposalJSONKeys.some((key) => Boolean(this.getCounterRevisionChange(changes, key)));
  }

  private static rebaseBrowserLocalFieldAfterSamePartySubmission({
    counterRevisionState,
    latestProposalJSON,
    proposalField,
  }: {
    counterRevisionState: ProposalCounterRevisionState;
    latestProposalJSON: ProposalJSON;
    proposalField: ProposalField;
  }): void {
    if (!this.latestProposalIsKnownSuccessorOfCounterRevision(latestProposalJSON, counterRevisionState)) {
      return;
    }

    for (const key of proposalField.proposalJSONKeys) {
      const change = this.getCounterRevisionChange(counterRevisionState.changes, key);
      const changeParty = counterRevisionState.changeParties?.[key];
      if (
        !change ||
        !changeParty ||
        counterRevisionState.changeOrigins?.[key] !== "browserLocal" ||
        !this.latestProposalWasSubmittedByParty({
          counterRevisionState,
          latestProposalJSON,
          party: changeParty,
        })
      ) {
        continue;
      }

      const latestValue = this.proposalJSONValue(latestProposalJSON, key);
      const collectionKey = this.counterRevisionCollectionKeyForField(proposalField, key);
      const rebasedBrowserValue = collectionKey
        ? this.applyBrowserLocalCollectionDelta({
            collectionKey,
            from: change.from,
            latest: latestValue,
            to: change.to,
          })
        : change.to;

      change.from = this.cloneJSONValue(latestValue);
      change.to = this.cloneJSONValue(rebasedBrowserValue ?? change.to);
      if (counterRevisionState.baseFieldValues) {
        counterRevisionState.baseFieldValues[key] = this.cloneJSONValue(latestValue);
      }
    }
  }

  private static latestProposalConsumesPersistedCounterRevisionField({
    counterRevisionState,
    latestProposalJSON,
    proposalField,
  }: {
    counterRevisionState: ProposalCounterRevisionState;
    latestProposalJSON: ProposalJSON;
    proposalField: ProposalField;
  }): boolean {
    if (!this.latestProposalIsKnownSuccessorOfCounterRevision(latestProposalJSON, counterRevisionState)) {
      return false;
    }

    const latestRevisionId = this.proposalRevisionId(latestProposalJSON);
    if (!latestRevisionId) return false;

    return proposalField.proposalJSONKeys.some((key) => {
      if (
        !this.getCounterRevisionChange(counterRevisionState.changes, key) ||
        counterRevisionState.changeOrigins?.[key] !== "persistedPartyRevision"
      ) {
        return false;
      }

      const changeParty = counterRevisionState.changeParties?.[key];
      if (!changeParty) return false;

      return this.latestProposalWasSubmittedByParty({
        counterRevisionState,
        latestProposalJSON,
        party: changeParty,
      });
    });
  }

  private static latestProposalWasSubmittedByParty({
    counterRevisionState,
    latestProposalJSON,
    party,
  }: {
    counterRevisionState: ProposalCounterRevisionState;
    latestProposalJSON: ProposalJSON;
    party: ProposalCounterRevisionChangeParty;
  }): boolean {
    const latestRevisionId = this.proposalRevisionId(latestProposalJSON);
    if (!latestRevisionId) return false;

    const submittedRevisionParties = new Set(
      (latestProposalJSON.negotiationOperations ?? [])
        .filter(
          (operation) =>
            operation.baseRevisionId === latestRevisionId && operation.kind === "ProposalRevisionSubmitted",
        )
        .map((operation) => operation.party),
    );
    if (submittedRevisionParties.size > 0) {
      return submittedRevisionParties.has(party);
    }

    // A completely reverted revision has no field operations. The immutable
    // predecessor edge proves this is a distinct submission, after which the
    // creator's team identifies which persisted party draft was consumed.
    const isDistinctImmutableSuccessor =
      latestProposalJSON.previousLatestRevisionId === counterRevisionState.baseRevisionId &&
      latestRevisionId !== counterRevisionState.baseRevisionId;
    const isInitialSubmissionOfSameRevision =
      latestRevisionId === counterRevisionState.baseRevisionId &&
      counterRevisionState.baseProposalJSON?.status === "AwaitingSubmission" &&
      Boolean(latestProposalJSON.status && latestProposalJSON.status !== "AwaitingSubmission");
    return (
      (isDistinctImmutableSuccessor || isInitialSubmissionOfSameRevision) &&
      this.proposalJSONCreatorParty(latestProposalJSON) === party
    );
  }

  private static proposalJSONCreatorParty(proposalJSON: ProposalJSON): ProposalCounterRevisionChangeParty | undefined {
    const creatorUserId = proposalJSON.creator?.userId;
    if (!creatorUserId) return undefined;

    if (
      proposalJSON.clientTeam?.leader?.userId === creatorUserId ||
      proposalJSON.clientTeam?.members?.some((member) => member.userId === creatorUserId) ||
      proposalJSON.clientReviewers?.some((reviewer) => reviewer.userId === creatorUserId)
    ) {
      return "Client";
    }

    if (
      proposalJSON.vendorTeam?.leader?.userId === creatorUserId ||
      proposalJSON.vendorTeam?.members?.some((member) => member.userId === creatorUserId) ||
      proposalJSON.vendorReviewers?.some((reviewer) => reviewer.userId === creatorUserId)
    ) {
      return "Vendor";
    }

    return undefined;
  }

  private static counterRevisionFieldStillApplies(
    proposalField: ProposalField,
    changes: ProposalCounterRevisionChangeSet,
    latestProposalJSON: ProposalJSON,
    counterRevisionState: ProposalCounterRevisionState,
  ): boolean {
    const latestProposalIsKnownSuccessor = this.latestProposalIsKnownSuccessorOfCounterRevision(
      latestProposalJSON,
      counterRevisionState,
    );

    return proposalField.proposalJSONKeys.every((key) => {
      const change = this.getCounterRevisionChange(changes, key);
      if (!change) return true;

      const latestValue = this.proposalJSONValue(latestProposalJSON, key);
      const rebasedField = this.counterRevisionFieldRebaseForLatest({
        change,
        key,
        latestProposalJSON,
        proposalField,
      });

      if (rebasedField) {
        return !_.isEqual(latestValue, rebasedField.value);
      }

      if (this.counterRevisionFieldValuesAreEqual(proposalField, key, latestValue, change.from)) {
        return true;
      }

      // A conflicting value in a confirmed successor revision must remain a
      // counter-line: the other party's newer value does not erase this
      // party's saved or browser-local work. Unrelated stale revision state is
      // still discarded, and an incoming value equal to `to` is already
      // incorporated by the server and can be pruned.
      return (
        latestProposalIsKnownSuccessor &&
        !this.counterRevisionFieldValuesAreEqual(proposalField, key, latestValue, change.to)
      );
    });
  }

  private static latestProposalIsKnownSuccessorOfCounterRevision(
    latestProposalJSON: ProposalJSON,
    counterRevisionState: ProposalCounterRevisionState,
  ): boolean {
    if (!counterRevisionState.hasCurrentRevisionIntent) return false;

    const latestRevisionId = this.proposalRevisionId(latestProposalJSON);
    const baseRevisionId = counterRevisionState.baseRevisionId;
    if (latestRevisionId && baseRevisionId === latestRevisionId) {
      return true;
    }

    // Durable lineage is authoritative. A mutable latestRevisionId redirect
    // can make an ancient cached source's proposalId equal the new revision;
    // it must not override a mismatch with the immutable predecessor edge.
    if (latestProposalJSON.previousLatestRevisionId) {
      return (
        latestProposalJSON.previousLatestRevisionId !== latestRevisionId &&
        latestProposalJSON.previousLatestRevisionId === baseRevisionId
      );
    }

    if (latestRevisionId && counterRevisionState.proposalId === latestRevisionId) {
      return true;
    }

    if (!baseRevisionId) return false;

    return (
      latestProposalJSON.supersedes?.id === baseRevisionId ||
      latestProposalJSON.supersedes?.latestRevisionId === baseRevisionId
    );
  }

  private static counterRevisionFieldHasIncomingConflict(
    proposalField: ProposalField,
    changes: ProposalCounterRevisionChangeSet,
    latestProposalJSON: ProposalJSON,
    counterRevisionState: ProposalCounterRevisionState,
  ): boolean {
    if (!this.latestProposalIsKnownSuccessorOfCounterRevision(latestProposalJSON, counterRevisionState)) {
      return false;
    }

    return proposalField.proposalJSONKeys.some((key) => {
      const change = this.getCounterRevisionChange(changes, key);
      if (!change || this.counterRevisionCollectionKeyForField(proposalField, key)) return false;

      const latestValue = this.proposalJSONValue(latestProposalJSON, key);
      return (
        !this.counterRevisionFieldValuesAreEqual(proposalField, key, latestValue, change.from) &&
        !this.counterRevisionFieldValuesAreEqual(proposalField, key, latestValue, change.to)
      );
    });
  }

  private static writeIncomingConflictBaseline({
    changes,
    counterRevisionState,
    latestProposalJSON,
    proposalField,
    proposalJSON,
  }: {
    changes: ProposalCounterRevisionChangeSet;
    counterRevisionState: ProposalCounterRevisionState;
    latestProposalJSON: ProposalJSON;
    proposalField: ProposalField;
    proposalJSON: LocalProposalJSON;
  }): void {
    if (
      !proposalJSON.supersedes ||
      !this.counterRevisionFieldHasIncomingConflict(proposalField, changes, latestProposalJSON, counterRevisionState)
    ) {
      return;
    }

    for (const key of proposalField.proposalJSONKeys) {
      const change = this.getCounterRevisionChange(changes, key);
      if (!change || this.counterRevisionCollectionKeyForField(proposalField, key)) continue;
      this.writeProposalJSONValue(proposalJSON.supersedes, key, change.to);
    }
  }

  private static writeCounterRevisionFieldValues(
    partyRevisionJSON: ProposalJSON,
    proposalField: ProposalField,
    changes: ProposalCounterRevisionChangeSet,
    latestProposalJSON: ProposalJSON,
  ): void {
    for (const key of proposalField.proposalJSONKeys) {
      const change = this.getCounterRevisionChange(changes, key);
      if (change) {
        this.writeProposalJSONValue(
          partyRevisionJSON,
          key,
          this.counterRevisionFieldValueForLatest({
            change,
            key,
            latestProposalJSON,
            proposalField,
          }),
        );
      }
    }
  }

  private static writeBaseFieldValuesForIncomingChanges({
    baseFieldValues,
    latestProposalJSON,
    partyRevisionJSON,
    proposalField,
  }: {
    baseFieldValues: Partial<Record<string, unknown>> | undefined;
    latestProposalJSON: ProposalJSON;
    partyRevisionJSON: ProposalJSON;
    proposalField: ProposalField;
  }): boolean {
    if (!baseFieldValues) return false;

    let wroteIncomingChange = false;
    for (const key of proposalField.proposalJSONKeys) {
      if (!this.counterRevisionStateHasBaseFieldValue(baseFieldValues, key)) continue;

      const baseValue = baseFieldValues[key];
      const latestValue = this.proposalJSONValue(latestProposalJSON, key);
      if (this.counterRevisionFieldValuesAreEqual(proposalField, key, latestValue, baseValue)) {
        continue;
      }

      this.writeProposalJSONValue(partyRevisionJSON, key, baseValue);
      wroteIncomingChange = true;
    }

    return wroteIncomingChange;
  }

  private static counterRevisionStateHasBaseFieldValue(
    baseFieldValues: Partial<Record<string, unknown>>,
    key: string,
  ): boolean {
    return Object.prototype.hasOwnProperty.call(baseFieldValues, key);
  }

  private static counterRevisionFieldDiffersFromLatest(
    proposalField: ProposalField,
    latestProposalJSON: ProposalJSON,
    partyRevisionJSON: ProposalJSON,
  ): boolean {
    return proposalField.proposalJSONKeys.some(
      (key) =>
        !this.counterRevisionFieldValuesAreEqual(
          proposalField,
          key,
          this.proposalJSONValue(latestProposalJSON, key),
          this.proposalJSONValue(partyRevisionJSON, key),
        ),
    );
  }

  private static counterRevisionFieldValueForLatest({
    change,
    key,
    latestProposalJSON,
    proposalField,
  }: {
    change: ProposalCounterRevisionFieldChange;
    key: string;
    latestProposalJSON: ProposalJSON;
    proposalField: ProposalField;
  }): unknown {
    return (
      this.counterRevisionFieldRebaseForLatest({
        change,
        key,
        latestProposalJSON,
        proposalField,
      })?.value ?? change.to
    );
  }

  private static counterRevisionFieldRebaseForLatest({
    change,
    key,
    latestProposalJSON,
    proposalField,
  }: {
    change: ProposalCounterRevisionFieldChange;
    key: string;
    latestProposalJSON: ProposalJSON;
    proposalField: ProposalField;
  }): { value: unknown } | undefined {
    const collectionKey = this.counterRevisionCollectionKeyForField(proposalField, key);
    if (!collectionKey) return undefined;

    const latest = this.proposalJSONValue(latestProposalJSON, key);
    if (_.isEqual(latest, change.from)) {
      return { value: change.to };
    }

    const rebased = this.rebaseCounterRevisionCollectionWrapper({
      collectionKey,
      from: change.from,
      latest,
      to: change.to,
    });
    return rebased ? { value: rebased } : undefined;
  }

  private static counterRevisionFieldValuesAreEqual(
    proposalField: ProposalField,
    key: string,
    left: unknown,
    right: unknown,
  ): boolean {
    if (this.proposalFieldUsesDocumentCollection(proposalField, key)) {
      return this.documentCollectionWrappersAreEqual(left, right);
    }

    return _.isEqual(left, right);
  }

  private static proposalFieldUsesDocumentCollection(proposalField: ProposalField, key: string): boolean {
    return (
      key === ProposalFieldName.ClientPolicies ||
      key === ProposalFieldName.VendorPolicies ||
      key === ProposalFieldName.Conflicts ||
      proposalField.isEqualTo(ProposalField.ClientPolicies) ||
      proposalField.isEqualTo(ProposalField.VendorPolicies) ||
      proposalField.isEqualTo(ProposalField.Conflicts)
    );
  }

  private static counterRevisionCollectionKeyForField(
    proposalField: ProposalField,
    key: string,
  ): CounterRevisionCollectionKey | undefined {
    if (this.proposalFieldUsesDocumentCollection(proposalField, key)) return "documents";
    if (key === ProposalFieldName.FeeSchedule || proposalField.isEqualTo(ProposalField.FeeSchedule)) {
      return "categories";
    }
    if (key === "vendorTeam" || proposalField.isEqualTo(ProposalField.Team)) return "members";

    return undefined;
  }

  private static documentCollectionWrappersAreEqual(left: unknown, right: unknown): boolean {
    if (!this.isDocumentCollectionWrapper(left) || !this.isDocumentCollectionWrapper(right)) {
      return _.isEqual(left, right);
    }
    if (left.isMasked || right.isMasked) return true;

    return this.documentCollectionsAreEqual(left.documents ?? [], right.documents ?? []);
  }

  private static documentCollectionsAreEqual(left: unknown[], right: unknown[]): boolean {
    if (left.length !== right.length) return false;

    const unmatchedRight = [...right];
    return left.every((document) => {
      const matchedIndex = unmatchedRight.findIndex((otherDocument) =>
        this.documentEntriesHaveSameIdentity(document, otherDocument),
      );
      if (matchedIndex === -1) return false;

      unmatchedRight.splice(matchedIndex, 1);
      return true;
    });
  }

  private static applyBrowserLocalCollectionDelta({
    collectionKey,
    from,
    latest,
    to,
  }: {
    collectionKey: CounterRevisionCollectionKey;
    from: unknown;
    latest: unknown;
    to: unknown;
  }): unknown | undefined {
    if (
      !this.isCounterRevisionCollectionWrapper(from, collectionKey) ||
      !this.isCounterRevisionCollectionWrapper(latest, collectionKey) ||
      !this.isCounterRevisionCollectionWrapper(to, collectionKey) ||
      from.isMasked ||
      latest.isMasked ||
      to.isMasked
    ) {
      return undefined;
    }

    const fromEntries = (from[collectionKey] as unknown[] | undefined) ?? [];
    const latestEntries = (latest[collectionKey] as unknown[] | undefined) ?? [];
    const toEntries = (to[collectionKey] as unknown[] | undefined) ?? [];
    const fromByIdentity = this.counterRevisionCollectionEntriesByIdentity(fromEntries, collectionKey);
    const latestByIdentity = this.counterRevisionCollectionEntriesByIdentity(latestEntries, collectionKey);
    const toByIdentity = this.counterRevisionCollectionEntriesByIdentity(toEntries, collectionKey);
    if (!fromByIdentity || !latestByIdentity || !toByIdentity) return undefined;

    const rebasedEntries = latestEntries.map((entry) => this.cloneJSONValue(entry));
    const findRebasedEntryIndex = (identity: string) =>
      rebasedEntries.findIndex(
        (entry) => this.counterRevisionCollectionEntryIdentity(entry, collectionKey) === identity,
      );

    for (const [identity, fromEntry] of fromByIdentity) {
      const toEntry = toByIdentity.get(identity);
      if (!toEntry) {
        const index = findRebasedEntryIndex(identity);
        if (index !== -1) rebasedEntries.splice(index, 1);
        continue;
      }
      if (_.isEqual(fromEntry, toEntry)) continue;

      const index = findRebasedEntryIndex(identity);
      if (index === -1) {
        rebasedEntries.push(this.cloneJSONValue(toEntry));
      } else {
        rebasedEntries[index] = this.cloneJSONValue(toEntry);
      }
    }

    for (const [identity, toEntry] of toByIdentity) {
      if (fromByIdentity.has(identity)) continue;

      const index = findRebasedEntryIndex(identity);
      if (index === -1) {
        rebasedEntries.push(this.cloneJSONValue(toEntry));
      } else {
        rebasedEntries[index] = this.cloneJSONValue(toEntry);
      }
    }

    const rebasedWrapper = this.cloneJSONValue(latest) as Record<string, unknown>;
    for (const propertyKey of new Set([...Object.keys(from), ...Object.keys(to)])) {
      if (propertyKey === "isMasked" || propertyKey === collectionKey) continue;
      if (!_.isEqual(from[propertyKey], to[propertyKey])) {
        rebasedWrapper[propertyKey] = this.cloneJSONValue(to[propertyKey]);
      }
    }
    rebasedWrapper[collectionKey] = rebasedEntries;
    rebasedWrapper.isMasked = false;
    return rebasedWrapper;
  }

  private static rebaseCounterRevisionCollectionWrapper({
    collectionKey,
    from,
    latest,
    to,
  }: {
    collectionKey: CounterRevisionCollectionKey;
    from: unknown;
    latest: unknown;
    to: unknown;
  }): unknown | undefined {
    if (
      !this.isCounterRevisionCollectionWrapper(from, collectionKey) ||
      !this.isCounterRevisionCollectionWrapper(latest, collectionKey) ||
      !this.isCounterRevisionCollectionWrapper(to, collectionKey) ||
      from.isMasked ||
      latest.isMasked ||
      to.isMasked
    ) {
      return undefined;
    }

    const rebasedEntries = this.rebaseCounterRevisionCollectionEntries({
      collectionKey,
      from: (from[collectionKey] as unknown[] | undefined) ?? [],
      latest: (latest[collectionKey] as unknown[] | undefined) ?? [],
      to: (to[collectionKey] as unknown[] | undefined) ?? [],
    });
    if (!rebasedEntries) return undefined;

    const rebasedWrapper = this.cloneJSONValue(from) as Record<string, unknown>;
    const wrapperPropertyKeys = new Set([...Object.keys(from), ...Object.keys(to)]);
    for (const propertyKey of wrapperPropertyKeys) {
      if (propertyKey === "isMasked" || propertyKey === collectionKey) continue;

      const fromProperty = from[propertyKey];
      const latestProperty = latest[propertyKey];
      const toProperty = to[propertyKey];
      if (!_.isEqual(fromProperty, toProperty) && _.isEqual(latestProperty, fromProperty)) {
        rebasedWrapper[propertyKey] = this.cloneJSONValue(toProperty);
      }
    }
    rebasedWrapper[collectionKey] = rebasedEntries;
    rebasedWrapper.isMasked = false;

    return rebasedWrapper;
  }

  private static rebaseCounterRevisionCollectionEntries({
    collectionKey,
    from,
    latest,
    to,
  }: {
    collectionKey: CounterRevisionCollectionKey;
    from: unknown[];
    latest: unknown[];
    to: unknown[];
  }): unknown[] | undefined {
    const fromByIdentity = this.counterRevisionCollectionEntriesByIdentity(from, collectionKey);
    const latestByIdentity = this.counterRevisionCollectionEntriesByIdentity(latest, collectionKey);
    const toByIdentity = this.counterRevisionCollectionEntriesByIdentity(to, collectionKey);
    if (!fromByIdentity || !latestByIdentity || !toByIdentity) return undefined;

    const rebasedEntries = from.map((entry) => this.cloneJSONValue(entry));
    const removeRebasedEntry = (identity: string) => {
      const index = rebasedEntries.findIndex(
        (entry) => this.counterRevisionCollectionEntryIdentity(entry, collectionKey) === identity,
      );
      if (index !== -1) rebasedEntries.splice(index, 1);
    };
    const replaceRebasedEntry = (identity: string, entry: unknown) => {
      const index = rebasedEntries.findIndex(
        (rebasedEntry) => this.counterRevisionCollectionEntryIdentity(rebasedEntry, collectionKey) === identity,
      );
      if (index !== -1) rebasedEntries[index] = this.cloneJSONValue(entry);
    };

    for (const [identity, fromEntry] of fromByIdentity) {
      const latestEntry = latestByIdentity.get(identity);
      const toEntry = toByIdentity.get(identity);

      if (!toEntry) {
        if (latestEntry && _.isEqual(latestEntry, fromEntry)) removeRebasedEntry(identity);
        continue;
      }

      if (!_.isEqual(toEntry, fromEntry) && latestEntry && _.isEqual(latestEntry, fromEntry)) {
        replaceRebasedEntry(identity, toEntry);
      }
    }

    for (const [identity, toEntry] of toByIdentity) {
      if (!fromByIdentity.has(identity) && !latestByIdentity.has(identity)) {
        rebasedEntries.push(this.cloneJSONValue(toEntry));
      }
    }

    return rebasedEntries;
  }

  private static counterRevisionCollectionEntriesByIdentity(
    entries: unknown[],
    collectionKey: CounterRevisionCollectionKey,
  ): Map<string, unknown> | undefined {
    const entriesByIdentity = new Map<string, unknown>();
    for (const entry of entries) {
      const identity = this.counterRevisionCollectionEntryIdentity(entry, collectionKey);
      if (!identity || entriesByIdentity.has(identity)) return undefined;

      entriesByIdentity.set(identity, entry);
    }

    return entriesByIdentity;
  }

  private static isCounterRevisionCollectionWrapper(
    value: unknown,
    collectionKey: CounterRevisionCollectionKey,
  ): value is { isMasked?: boolean } & Record<string, unknown> {
    if (!value || typeof value !== "object" || !("isMasked" in value)) return false;

    const collectionValue = (value as Record<string, unknown>)[collectionKey];
    return collectionValue === undefined || Array.isArray(collectionValue);
  }

  private static counterRevisionCollectionEntryIdentity(
    value: unknown,
    collectionKey: CounterRevisionCollectionKey,
  ): string | undefined {
    switch (collectionKey) {
      case "categories":
        return this.objectStringProperty(value, "id");
      case "documents":
        return this.documentEntryIdentity(value);
      case "members":
        return this.entityMemberEntryIdentity(value);
    }
  }

  private static isDocumentCollectionWrapper(value: unknown): value is { documents?: unknown[]; isMasked?: boolean } {
    return Boolean(value && typeof value === "object" && "isMasked" in value);
  }

  private static documentEntriesHaveSameIdentity(left: unknown, right: unknown): boolean {
    const leftIdentity = this.documentEntryIdentity(left);
    const rightIdentity = this.documentEntryIdentity(right);
    return Boolean(leftIdentity && rightIdentity && leftIdentity === rightIdentity);
  }

  private static documentEntryIdentity(value: unknown): string | undefined {
    if (!value || typeof value !== "object") return undefined;

    const entry = value as {
      documentId?: unknown;
      documentInfo?: { id?: unknown };
      id?: unknown;
    };
    return this.stringValue(entry.documentId) ?? this.stringValue(entry.documentInfo?.id) ?? this.stringValue(entry.id);
  }

  private static entityMemberEntryIdentity(value: unknown): string | undefined {
    if (!value || typeof value !== "object") return undefined;

    const entry = value as {
      id?: unknown;
      legalEntityId?: unknown;
      userId?: unknown;
    };
    return this.stringValue(entry.userId) ?? this.stringValue(entry.id) ?? this.stringValue(entry.legalEntityId);
  }

  private static objectStringProperty(value: unknown, propertyKey: string): string | undefined {
    if (!value || typeof value !== "object") return undefined;

    return this.stringValue((value as Record<string, unknown>)[propertyKey]);
  }

  private static stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined;
  }

  private static addMaterializedUnresolvedFields(
    unresolvedFields: Set<ProposalFieldNameValue>,
    proposalField: ProposalField,
    sourceUnresolvedFields: ProposalFieldNameValue[] | undefined,
  ): void {
    const fieldIsUnresolved = sourceUnresolvedFields?.some((fieldName) =>
      proposalField.unresolvedFieldNames.includes(fieldName),
    );
    if (fieldIsUnresolved) {
      this.addUnresolvedFieldNames(unresolvedFields, proposalField.unresolvedFieldNames);
    }
  }

  private static extractCounterRevisionChangeSet(
    baseProposalJSON: ProposalJSON,
    partyRevisionJSON: ProposalJSON,
  ): ProposalCounterRevisionChangeSet | undefined {
    const changes: ProposalCounterRevisionChangeSet = {};

    for (const proposalField of ProposalField.CounterRevisionFields) {
      for (const key of proposalField.proposalJSONKeys) {
        const from = this.proposalJSONValue(baseProposalJSON, key);
        const to = this.proposalJSONValue(partyRevisionJSON, key);
        if (this.counterRevisionFieldValuesAreEqual(proposalField, key, from, to)) {
          continue;
        }

        changes[key] = {
          from: this.cloneJSONValue(from),
          to: this.cloneJSONValue(to),
        };
      }
    }

    return this.counterRevisionChangeSetHasChanges(changes) ? changes : undefined;
  }

  private static extractCounterRevisionBaseValues(baseProposalJSON: ProposalJSON): Partial<Record<string, unknown>> {
    const baseFieldValues: Partial<Record<string, unknown>> = {};

    for (const proposalField of ProposalField.CounterRevisionFields) {
      for (const key of proposalField.proposalJSONKeys) {
        baseFieldValues[key] = this.cloneJSONValue(this.proposalJSONValue(baseProposalJSON, key));
      }
    }

    return baseFieldValues;
  }

  private static removeCounterRevisionFieldChanges(
    changes: ProposalCounterRevisionChangeSet,
    proposalField: ProposalField,
    counterRevisionState?: ProposalCounterRevisionState,
  ): void {
    for (const key of proposalField.proposalJSONKeys) {
      delete changes[key];
      if (counterRevisionState?.changeOrigins) {
        delete counterRevisionState.changeOrigins[key];
      }
      if (counterRevisionState?.changeParties) {
        delete counterRevisionState.changeParties[key];
      }
    }

    if (counterRevisionState?.changeOrigins && Object.keys(counterRevisionState.changeOrigins).length === 0) {
      counterRevisionState.changeOrigins = undefined;
    }
    if (counterRevisionState?.changeParties && Object.keys(counterRevisionState.changeParties).length === 0) {
      counterRevisionState.changeParties = undefined;
    }
  }

  private static pruneCounterRevisionUnresolvedFields(
    unresolvedFields: ProposalFieldNameValue[] | undefined,
    removedUnresolvedFields: Set<ProposalFieldNameValue>,
  ): ProposalFieldNameValue[] | undefined {
    return unresolvedFields?.filter((fieldName) => !removedUnresolvedFields.has(fieldName));
  }

  private static proposalJSONHasCounterRevisionState(proposalJSON: LocalProposalJSON | undefined): boolean {
    return Boolean(
      proposalJSON?.existingRedlineJSON || proposalJSON?.hasLocallyUnsavedRedlineChanges || proposalJSON?.partyRevision,
    );
  }

  private static counterRevisionStateHasChanges(
    counterRevisionState: ProposalCounterRevisionState | undefined,
  ): boolean {
    return this.counterRevisionChangeSetHasChanges(counterRevisionState?.changes);
  }

  private static counterRevisionStateHasLocalMarkers(
    counterRevisionState: ProposalCounterRevisionState | undefined,
  ): boolean {
    return Boolean(counterRevisionState?.existingRedlineJSON || counterRevisionState?.hasLocallyUnsavedRedlineChanges);
  }

  private static counterRevisionStateHasContent(counterRevisionState: ProposalCounterRevisionState): boolean {
    return (
      this.counterRevisionStateHasChanges(counterRevisionState) ||
      this.counterRevisionStateHasLocalMarkers(counterRevisionState)
    );
  }

  private static createTeamRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
    missingRevisedEntriesAreRejected: boolean;
  }): FieldRedlineArray<EntityMember> | MaskedTeam {
    const { originalVersion, revisedVersion, currentVersion, missingRevisedEntriesAreRejected } = params;
    const teamIsMasked =
      Masked.isMasked(originalVersion.vendorTeam) ||
      Masked.isMasked(revisedVersion.vendorTeam) ||
      Masked.isMasked(currentVersion?.vendorTeam);

    if (teamIsMasked) {
      return new MaskedTeam();
    }

    const originalEntries =
      originalVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [];
    const revisedEntries =
      revisedVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [];

    let currentEntries: EntityMember[] | undefined;
    if (currentVersion === undefined) {
      currentEntries = undefined;
    } else if (currentVersion.unresolvedFields.has(ProposalFieldName.Team)) {
      currentEntries = [];
    } else {
      currentEntries =
        currentVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [];
    }

    return FieldRedlineArray.create<EntityMember>({
      field: ProposalField.Team,
      entryField: ProposalField.TeamMember,
      originalEntries,
      revisedEntries,
      currentEntries,
      options: { missingRevisedEntriesAreRejected },
    });
  }

  private static createDocumentsRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
    waivingConflictsChecks: boolean;
    missingRevisedEntriesAreRejected: boolean;
  }): RedlinedDocuments {
    const {
      originalVersion,
      revisedVersion,
      currentVersion,
      waivingConflictsChecks,
      missingRevisedEntriesAreRejected,
    } = params;
    const clientPoliciesMasked =
      Masked.isMasked(originalVersion?.clientPolicies) ||
      Masked.isMasked(revisedVersion.clientPolicies) ||
      Masked.isMasked(currentVersion?.clientPolicies);
    const vendorPoliciesMasked =
      Masked.isMasked(originalVersion?.vendorPolicies) ||
      Masked.isMasked(revisedVersion.vendorPolicies) ||
      Masked.isMasked(currentVersion?.vendorPolicies);
    const conflictsMasked =
      Masked.isMasked(originalVersion?.conflicts) ||
      Masked.isMasked(revisedVersion.conflicts) ||
      Masked.isMasked(currentVersion?.conflicts);

    const clientPoliciesUnresolved =
      currentVersion?.unresolvedFields.has(ProposalFieldName.ClientPolicies) ||
      currentVersion?.unresolvedFields.has(ProposalFieldName.Policies);
    const vendorPoliciesUnresolved =
      currentVersion?.unresolvedFields.has(ProposalFieldName.VendorPolicies) ||
      currentVersion?.unresolvedFields.has(ProposalFieldName.Policies);
    const conflictsUnresolved = currentVersion?.unresolvedFields.has(ProposalFieldName.Conflicts);

    let currentClientPolicies: WorkDocument[] | undefined;
    if (currentVersion === undefined) {
      currentClientPolicies = undefined;
    } else if (clientPoliciesUnresolved) {
      currentClientPolicies = undefined;
    } else {
      currentClientPolicies = (currentVersion.clientPolicies as WorkDocument[]) ?? [];
    }

    let currentVendorPolicies: WorkDocument[] | undefined;
    if (currentVersion === undefined) {
      currentVendorPolicies = undefined;
    } else if (vendorPoliciesUnresolved) {
      currentVendorPolicies = undefined;
    } else {
      currentVendorPolicies = (currentVersion.vendorPolicies as WorkDocument[]) ?? [];
    }

    let currentConflicts: WorkDocument[] | undefined;
    if (currentVersion === undefined) {
      currentConflicts = undefined;
    } else if (conflictsUnresolved) {
      currentConflicts = undefined;
    } else {
      currentConflicts = (currentVersion.conflicts as WorkDocument[]) ?? [];
    }

    const documentsRedline = {
      clientPolicies: clientPoliciesMasked
        ? new MaskedContracts()
        : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.ClientPolicies,
            entryField: ProposalField.ClientPolicy,
            originalEntries: originalVersion.clientPolicies,
            revisedEntries: revisedVersion.clientPolicies,
            currentEntries: currentClientPolicies,
            options: { missingRevisedEntriesAreRejected },
          }),
      vendorPolicies: vendorPoliciesMasked
        ? new MaskedContracts()
        : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.VendorPolicies,
            entryField: ProposalField.VendorPolicy,
            originalEntries: originalVersion.vendorPolicies,
            revisedEntries: revisedVersion.vendorPolicies,
            currentEntries: currentVendorPolicies,
            options: { missingRevisedEntriesAreRejected },
          }),
      conflicts: conflictsMasked
        ? new MaskedContracts()
        : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.Conflicts,
            entryField: ProposalField.ConflictsDocument,
            originalEntries: originalVersion.conflicts,
            revisedEntries: revisedVersion.conflicts,
            currentEntries: currentConflicts,
            options: { missingRevisedEntriesAreRejected },
          }),
    };

    if (waivingConflictsChecks && Masked.isNotMasked(documentsRedline.conflicts)) {
      documentsRedline.conflicts = documentsRedline.conflicts.acceptAll();
    }
    return documentsRedline;
  }
}
